/**
 * S3 presigned POST Policy（DC-7650）でのアップロード共通ヘルパー。
 *
 * backend が `content-length-range` でサイズ上限をサーバーサイド強制し、署名済みの
 * `fields`（key / Content-Type / x-amz-meta-* / tagging / policy / signature 等）を返す。
 * FE は fields を素通しで multipart/form-data に詰め、`file` を最後に置いて POST するだけでよい。
 */

/** presigned POST 発行 API の戻り値（FE が S3 へ POST する際に必要な部分）。 */
export type PresignedPostTarget = {
  url: string;
  fields: Record<string, string>;
};

/** presigned POST の失敗（非 2xx）。S3 は 4xx で XML エラー（EntityTooLarge 等）を返す。 */
export class PresignedPostUploadError extends Error {
  constructor(readonly status: number, /** S3 XML の `<Code>`。取得できなかった場合は undefined。例: 'EntityTooLarge' */
  readonly s3Code: string | undefined, message: string) {
    super(message);
    this.name = 'PresignedPostUploadError';
  }
}

// i18n 化は別タスク（F-1）。ここでは silent failure を防ぐためのユーザー提示可能な暫定文言。
const messageForS3Code = (s3Code: string | undefined): string => s3Code === 'EntityTooLarge' ? 'ファイルサイズが上限を超えています' : 'アップロードに失敗しました';
const parseS3ErrorCode = async (res: Response): Promise<string | undefined> => {
  try {
    const text = await res.text();
    return /<Code>([^<]+)<\/Code>/.exec(text)?.[1];
  } catch {
    return undefined;
  }
};

/**
 * presigned POST Policy で発行された `url` / `fields` を使い S3 へ multipart アップロードする。
 *
 * - `fields` を全件 append したのち `file` を **最後** に append する（S3 仕様: file 以降のフィールドは無視される）。
 * - リクエストに `Content-Type` ヘッダーは付けない（FormData が multipart boundary を自動設定する）。
 *   オブジェクトの Content-Type は `fields['Content-Type']`（backend 署名済み）で決まる。
 * - 成功は 204（backend は `success_action_status` 未設定）。非 2xx は {@link PresignedPostUploadError} を throw する。
 *
 * @param filename FormData の file パートに付与する名前。Blob でも名前を欠落させないため既定 'file'。
 */
export async function uploadViaPresignedPost(url: string, fields: Record<string, string>, file: Blob, filename = 'file'): Promise<void> {
  const form = new FormData();
  for (const [key, value] of Object.entries(fields)) {
    form.append(key, value);
  }
  form.append('file', file, filename);
  const res = await fetch(url, {
    method: 'POST',
    body: form
  });
  if (!res.ok) {
    const s3Code = await parseS3ErrorCode(res);
    throw new PresignedPostUploadError(res.status, s3Code, messageForS3Code(s3Code));
  }
}